import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/feature/EmptyState";
import { reviews } from "@/mocks/reviews";

export default function ReviewDetail() {
  const { id } = useParams<{ id: string }>();
  const review = reviews.find((r) => r.id === id);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!review) {
    return (
      <div className="w-full px-4 md:px-8 py-24">
        <EmptyState
          icon="ri-error-warning-line"
          title="该影评已被删除或不存在"
          description="你可以返回影评列表，浏览其他精彩内容。"
        />
        <div className="flex justify-center">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回影评列表
          </Link>
        </div>
      </div>
    );
  }

  const related = reviews.filter((r) => r.id !== review.id).slice(0, 3);

  const doShare = (channel: string) => {
    setShareOpen(false);
    setToast(`已模拟分享到「${channel}」，感谢你的传播！`);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板（模拟）");
    } catch {
      setToast("链接已复制（模拟）");
    }
    setShareOpen(false);
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-secondary-500 mb-4 flex-wrap">
            <Link to="/" className="hover:text-accent-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/reviews" className="hover:text-accent-500 transition-colors">
              影评与专题
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 truncate max-w-[12rem]">{review.title}</span>
          </nav>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs">
            <i className="ri-price-tag-3-line" />
            {review.category}
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
            {review.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-secondary-500">
            <span className="inline-flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-foreground-950 text-xs">
                {review.author.slice(-2)}
              </span>
              {review.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {review.date}
            </span>
            <span className="inline-flex items-center gap-1.5 text-accent-500 font-medium">
              <i className="ri-star-fill text-xs" />
              {review.rating.toFixed(1)} / 10
            </span>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <article className="lg:col-span-2 max-w-3xl">
            <figure className="w-full aspect-video rounded-lg overflow-hidden bg-background-200 mb-8">
              <img
                src={review.cover}
                alt={review.title}
                title={`${review.title} - 色戒影评`}
                className="w-full h-full object-cover object-top"
              />
            </figure>

            <blockquote className="border-l-4 border-accent-500 bg-background-100 rounded-r-lg px-5 py-4 mb-8">
              <p className="font-heading text-base md:text-lg text-foreground-900 italic leading-relaxed">
                「{review.quote}」
              </p>
            </blockquote>

            <div className="space-y-5">
              {review.content.map((para, i) => (
                <p key={i} className="text-sm md:text-[15px] leading-loose text-foreground-700">
                  {para}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {review.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to="/reviews"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-background-100 border border-background-200 text-foreground-800 text-sm hover:border-accent-500 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                返回影评列表
              </Link>
              <button
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-line" />
                分享这篇影评
              </button>
            </div>
          </article>

          <aside className="space-y-6">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <div className="flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                <h3 className="font-heading text-lg font-bold text-foreground-950">相关推荐</h3>
              </div>
              <div className="mt-4 space-y-4">
                {related.map((r) => (
                  <Link key={r.id} to={`/reviews/detail/${r.id}`} className="group flex gap-3">
                    <div className="w-24 shrink-0 aspect-video rounded-md overflow-hidden bg-background-200">
                      <img
                        src={r.cover}
                        alt={r.title}
                        title={`${r.title} - 色戒影评`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm text-foreground-950 group-hover:text-accent-500 transition-colors line-clamp-2 leading-snug">
                        {r.title}
                      </h4>
                      <p className="mt-1 text-xs text-secondary-500">{r.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-primary-900 text-foreground-950 p-5">
              <h3 className="font-heading text-base font-bold">看完影评，想再看一遍？</h3>
              <Link
                to="/watch"
                className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-fill" />
                前往播放页
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {shareOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-background-50/70 animate-fade-in"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg bg-background-100 border border-background-200 p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">分享影评</h3>
              <button
                onClick={() => setShareOpen(false)}
                aria-label="关闭"
                className="w-8 h-8 flex items-center justify-center rounded-md text-secondary-500 hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <p className="mt-2 text-xs text-secondary-500">分享功能为前端演示，不会真实发送内容。</p>
            <div className="mt-5 grid grid-cols-4 gap-3">
              {[
                { icon: "ri-weibo-fill", label: "微博" },
                { icon: "ri-wechat-fill", label: "微信" },
                { icon: "ri-qq-fill", label: "QQ" },
                { icon: "ri-link", label: "复制链接" },
              ].map((s) => (
                <button
                  key={s.label}
                  onClick={() => (s.label === "复制链接" ? copyLink() : doShare(s.label))}
                  className="flex flex-col items-center gap-2 py-3 rounded-md bg-background-50 border border-background-200 hover:border-accent-500 transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-xl text-accent-500`} />
                  <span className="text-xs text-foreground-700">{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-accent-500 text-primary-950 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}